import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  BellOff,
  ImageIcon,
  KeyRound,
  Lock,
  Loader2,
  LogOut,
  PenTool,
  Power,
  RefreshCw,
  Trash2,
  Upload,
  Volume2,
} from 'lucide-react';
import { apiGet, apiPatch, apiPost, apiUpload, apiDelete, getFileBlob } from '../shared/apiClient';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import type { Squadra } from '../shared/types';
import {
  getNotificationPermission,
  subscribeUserToPush,
  unsubscribeUserFromPush,
  sendTestPushNotification,
} from '../utils/pushNotifications';
import { playEmergencyAlert } from '../utils/audioAlert';
import { CollapsibleSection } from '../components/CollapsibleSection';

export function ImpostazioniPage() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported' | 'insecure_context'>(
    getNotificationPermission()
  );
  const [pushLoading, setPushLoading] = useState(false);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePwdLoading, setChangePwdLoading] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oldPassword) {
      toast({ type: 'error', message: 'Inserisci la tua password attuale' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ type: 'error', message: 'La nuova password deve contenere almeno 6 caratteri' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ type: 'error', message: 'La nuova password e la conferma non coincidono' });
      return;
    }

    setChangePwdLoading(true);
    try {
      await apiPost('/auth/change-password', { oldPassword, newPassword });
      toast({ type: 'success', message: 'Password aggiornata con successo' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err?.response?.data?.errore || err?.message || 'Errore cambio password';
      toast({ type: 'error', message: msg });
    } finally {
      setChangePwdLoading(false);
    }
  };

  useEffect(() => {
    setPermission(getNotificationPermission());
  }, []);

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      const res = await subscribeUserToPush();
      setPermission(getNotificationPermission());
      if (res.ok) {
        toast({ type: 'success', message: 'Notifiche push attivate con successo!' });
        playEmergencyAlert();
      } else {
        toast({ type: 'error', message: res.message || 'Impossibile attivare le notifiche' });
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      const res = await unsubscribeUserFromPush();
      if (res.ok) {
        toast({ type: 'success', message: 'Notifiche push disattivate' });
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestAlert = async () => {
    // 1. Suono e vibrazione immediata
    playEmergencyAlert();
    // 2. Invio notifica push reale tramite server
    toast({ type: 'info', message: 'Invio notifica di prova in corso…' });
    const res = await sendTestPushNotification();
    if (res.ok) {
      toast({ type: 'success', message: `Notifica di test inviata a ${res.inviati || 1} dispositivo/i!` });
    } else {
      toast({ type: 'error', message: res.error || 'Errore invio notifica test' });
    }
  };

  const squadraQuery = useQuery({
    queryKey: ['squadra-me'],
    queryFn: async () => {
      if (!user?.squadraId) return null;
      const { data } = await apiGet<Squadra>('/squadre/me');
      return data;
    },
  });

  const isDisponibile = squadraQuery.data?.disponibile ?? false;

  // Stato e caricamento anteprima firma
  const [firmaPreviewUrl, setFirmaPreviewUrl] = useState<string | null>(null);
  const [isUploadingFirma, setIsUploadingFirma] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (squadraQuery.data?.firmaImmagine) {
      let active = true;
      getFileBlob('/squadre/me/firma')
        .then(({ url }) => {
          if (active) setFirmaPreviewUrl(url);
        })
        .catch(() => {
          if (active) setFirmaPreviewUrl(null);
        });
      return () => {
        active = false;
      };
    } else {
      setFirmaPreviewUrl(null);
    }
  }, [squadraQuery.data?.firmaImmagine]);

  const uploadFirmaMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('firma', file);
      const { data } = await apiUpload<Squadra>('/squadre/me/firma', formData);
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['squadra-me'], updated);
      toast({ type: 'success', message: 'Firma della squadra caricata con successo' });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errore || 'Errore caricamento firma';
      toast({ type: 'error', message: msg });
    },
  });

  const deleteFirmaMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiDelete<Squadra>('/squadre/me/firma');
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['squadra-me'], updated);
      setFirmaPreviewUrl(null);
      toast({ type: 'success', message: 'Firma rimossa con successo' });
    },
    onError: () => {
      toast({ type: 'error', message: 'Errore rimozione firma' });
    },
  });

/**
 * Elabora la foto della firma:
 * 1. Carica l'immagine su un canvas HTML5 in memoria.
 * 2. Scala proporzionalmente a max 1200px per leggerezza e performance.
 * 3. Analizza la luminosità pixel per pixel:
 *    - Rimuove lo sfondo del foglio (luce, ombre, grigi) rendendolo trasparente (alpha = 0).
 *    - Mantiene il tratto della penna rendendolo nitido e ad alto contrasto.
 *    - Applica una rampa di trasparenza morbida per l'anti-aliasing naturale dei bordi.
 * 4. Esporta un file PNG leggero con sfondo trasparente.
 */
function estraiTrattoFirmaTrasparente(file: File): Promise<File> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(file);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(file);
      img.onload = () => {
        try {
          const maxDim = 1200;
          let w = img.width;
          let h = img.height;
          if (w > maxDim || h > maxDim) {
            if (w > h) {
              h = Math.round((h * maxDim) / w);
              w = maxDim;
            } else {
              w = Math.round((w * maxDim) / h);
              h = maxDim;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) {
            resolve(file);
            return;
          }

          ctx.drawImage(img, 0, 0, w, h);
          const imgData = ctx.getImageData(0, 0, w, h);
          const data = imgData.data;

          const T_WHITE = 205;
          const T_DARK = 135;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const lum = 0.299 * r + 0.587 * g + 0.114 * b;

            if (lum >= T_WHITE) {
              // Sfondo bianco/chiaro del foglio -> trasparente al 100%
              data[i + 3] = 0;
            } else if (lum <= T_DARK) {
              // Tratto scuro della penna -> opaco al 100% e inchiostro nitido scuro
              data[i] = Math.min(r, 20);
              data[i + 1] = Math.min(g, 25);
              data[i + 2] = Math.min(b, 40);
              data[i + 3] = 255;
            } else {
              // Transizione morbida anti-alias
              const alphaRatio = (T_WHITE - lum) / (T_WHITE - T_DARK);
              const a = Math.round(255 * Math.pow(alphaRatio, 1.2));
              data[i] = Math.min(r, 30);
              data[i + 1] = Math.min(g, 35);
              data[i + 2] = Math.min(b, 50);
              data[i + 3] = Math.max(0, Math.min(255, a));
            }
          }

          ctx.putImageData(imgData, 0, 0);

          canvas.toBlob((blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const cleanName = file.name.replace(/\.[^.]+$/, '') + '-trasparente.png';
            const transparentFile = new File([blob], cleanName, { type: 'image/png' });
            resolve(transparentFile);
          }, 'image/png');
        } catch (e) {
          console.warn('Fallback al file originale:', e);
          resolve(file);
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingFirma(true);
    try {
      const processedPng = await estraiTrattoFirmaTrasparente(file);
      await uploadFirmaMutation.mutateAsync(processedPng);
    } catch {
      toast({ type: 'error', message: 'Errore durante il caricamento della firma' });
    } finally {
      setIsUploadingFirma(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const r = await apiPatch('/squadre/me/disponibile', { disponibile: next });
      return r;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: ['squadra-me'] });
      const prev = queryClient.getQueryData<Squadra>(['squadra-me']);
      if (prev) queryClient.setQueryData<Squadra>(['squadra-me'], { ...prev, disponibile: next });
      return { prev };
    },
    onError: (_e, _next, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['squadra-me'], ctx.prev);
      toast({ type: 'error', message: 'Impossibile aggiornare la disponibilità' });
    },
    onSuccess: () => {
      toast({ type: 'success', message: 'Stato disponibilità aggiornato' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['squadra-me'] });
    },
  });

  const toggleDisponibile = () => {
    toggleMutation.mutate(!isDisponibile);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* ── STATO DI SERVIZIO (collassato di default) ── */}
      <CollapsibleSection
        icon={Power}
        iconClass="bg-emerald-100 text-emerald-700"
        title="Stato di Servizio"
        subtitle="Visibilità per l'assegnazione automatica e per la sala operativa"
      >
        <div
          className={`rounded-2xl border p-4 transition-colors ${
            isDisponibile
              ? 'bg-linear-to-br from-emerald-50 to-teal-50/60 border-emerald-300'
              : 'bg-slate-50 border-slate-200'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span
                  className={`w-3 h-3 rounded-full ${
                    isDisponibile ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'
                  }`}
                />
                <span className="font-extrabold text-base sm:text-lg text-slate-900">
                  {isDisponibile ? 'Disponibile in Servizio' : 'Non Disponibile / Fuori Servizio'}
                </span>
              </div>
              <p className="text-xs text-slate-600">
                {isDisponibile
                  ? 'La tua squadra appare ONLINE nella sala operativa e può ricevere chiamate.'
                  : 'La tua squadra appare OFFLINE per nuove assegnazioni.'}
              </p>
            </div>

            <button
              type="button"
              className={`px-4 py-2.5 rounded-xl font-bold text-xs shadow-xs transition active:scale-[0.98] ${
                isDisponibile
                  ? 'bg-white hover:bg-slate-100 text-slate-800 border border-slate-300'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
              onClick={toggleDisponibile}
              disabled={toggleMutation.isPending}
            >
              <span className="flex items-center gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${toggleMutation.isPending ? 'animate-spin' : ''}`} />
                <span>{isDisponibile ? 'Metti in pausa' : 'Torna online'}</span>
              </span>
            </button>
          </div>
        </div>
      </CollapsibleSection>

      {/* ── NOTIFICHE PUSH & SEGNALE ACUSTICO (collassato di default) ── */}
      <CollapsibleSection
        icon={Bell}
        iconClass="bg-amber-100 text-amber-700"
        title="Notifiche di Intervento"
        subtitle="Avvisi sonori e visivi quando viene assegnata una chiamata"
        headerRight={
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full ${
              permission === 'granted'
                ? 'badge-verde'
                : permission === 'denied'
                ? 'badge-rosso'
                : permission === 'insecure_context'
                ? 'badge-amber'
                : 'badge-amber'
            }`}
          >
            {permission === 'granted'
              ? 'Abilitate'
              : permission === 'denied'
              ? 'Bloccate'
              : permission === 'insecure_context'
              ? 'Richiede HTTPS'
              : permission === 'unsupported'
              ? 'Non supportate'
              : 'Da attivare'}
          </span>
        }
      >
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Volume2 className="w-5 h-5 text-semaforo-verde-700 shrink-0 mt-0.5" />
            <div className="text-xs text-slate-700 space-y-1">
              <p className="font-bold text-slate-900">Segnale Acustico & Vibrazione Nativa</p>
              <p className="text-slate-600 leading-relaxed">
                All'assegnazione di una chiamata dalla sala operativa, lo smartphone squillerà con un segnale acustico e mostrerà la notifica con l'incrocio dell'impianto.
              </p>
            </div>
          </div>

          {permission === 'denied' && (
            <div className="p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-800 space-y-1">
              <p className="font-bold">⚠️ Notifiche bloccate nel browser</p>
              <p className="leading-relaxed">
                Per abilitarle sul tuo smartphone: tocca l'icona delle <strong>impostazioni / lucchetto 🔒</strong> a sinistra della barra degli indirizzi in alto, vai su <strong>"Permessi"</strong> o <strong>"Notifiche"</strong> e seleziona <strong>"Consenti"</strong>.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-slate-200/80">
            {permission !== 'granted' ? (
              <button
                type="button"
                className="btn-primary justify-center py-2.5 text-xs font-bold gap-1.5"
                onClick={handleEnablePush}
                disabled={pushLoading}
              >
                <Bell className="w-4 h-4" />
                <span>{pushLoading ? 'Attivazione…' : 'Attiva Notifiche su questo Telefono'}</span>
              </button>
            ) : (
              <button
                type="button"
                className="btn-outline justify-center py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-100"
                onClick={handleDisablePush}
                disabled={pushLoading}
              >
                <BellOff className="w-4 h-4" />
                <span>Disattiva Notifiche</span>
              </button>
            )}

            <button
              type="button"
              className="btn-outline justify-center py-2.5 text-xs font-bold text-amber-900 border-amber-300 bg-amber-50/60 hover:bg-amber-100/70 gap-1.5"
              onClick={handleTestAlert}
            >
              <Volume2 className="w-4 h-4 text-amber-700" />
              <span>Prova Suono & Vibrazione</span>
            </button>
          </div>
        </div>
      </CollapsibleSection>

      {/* ── FIRMA DELLA SQUADRA (collassato di default) ── */}
      <CollapsibleSection
        icon={PenTool}
        iconClass="bg-blue-100 text-blue-700"
        title="Firma della Squadra"
        subtitle="Immagine della firma da apporre nei report PDF"
        headerRight={
          squadraQuery.data?.firmaImmagine ? (
            <span className="badge-verde text-xs font-bold flex items-center gap-1">
              <span>Presente</span>
            </span>
          ) : undefined
        }
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelected}
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
        />

        {squadraQuery.data?.firmaImmagine ? (
          <div className="space-y-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3.5 flex flex-col items-center justify-center gap-2">
              <div
                className="rounded-xl border border-slate-200/80 p-3 shadow-2xs flex items-center justify-center w-full max-w-xs min-h-[70px] max-h-[110px]"
                style={{
                  backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
                  backgroundSize: '8px 8px',
                  backgroundColor: '#f8fafc',
                }}
              >
                {firmaPreviewUrl ? (
                  <img
                    src={firmaPreviewUrl}
                    alt="Firma squadra"
                    className="max-h-20 max-w-full object-contain"
                  />
                ) : (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Caricamento anteprima firma...</span>
                  </div>
                )}
              </div>
              <div className="inline-flex items-center gap-1 text-[10.5px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                <span>✓ Tratto penna isolato & sfondo trasparente per PDF</span>
              </div>
              <p className="text-[11px] text-slate-500 text-center">
                Questa firma sostituisce il nome della squadra nel riquadro <strong>FIRMA</strong> del report PDF.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-outline flex-1 justify-center py-2.5 text-xs font-bold gap-1.5"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingFirma || uploadFirmaMutation.isPending}
              >
                <Upload className="w-3.5 h-3.5" />
                <span>{isUploadingFirma ? 'Caricamento…' : 'Sostituisci Firma'}</span>
              </button>

              <button
                type="button"
                className="btn-outline justify-center py-2.5 px-3 text-xs font-bold text-rose-600 border-rose-200 hover:bg-rose-50 gap-1.5"
                onClick={() => {
                  if (confirm('Vuoi davvero rimuovere l\'immagine della firma?')) {
                    deleteFirmaMutation.mutate();
                  }
                }}
                disabled={deleteFirmaMutation.isPending}
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Rimuovi</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-4 text-center space-y-2.5">
            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto">
              <ImageIcon className="w-5 h-5" />
            </div>
            <div className="space-y-0.5">
              <p className="font-bold text-xs sm:text-sm text-slate-800">Nessuna firma impostata</p>
              <p className="text-[11px] text-slate-500 max-w-sm mx-auto">
                Carica una foto o immagine con la firma per apporla direttamente sul report PDF dell'intervento.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary mx-auto py-2 px-4 text-xs font-bold gap-1.5 shadow-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingFirma || uploadFirmaMutation.isPending}
            >
              <Upload className="w-3.5 h-3.5" />
              <span>{isUploadingFirma ? 'Caricamento…' : 'Carica Immagine Firma'}</span>
            </button>
          </div>
        )}
      </CollapsibleSection>

      {/* ── CAMBIO PASSWORD UTENTE ── */}
      <CollapsibleSection
        icon={KeyRound}
        iconClass="bg-indigo-100 text-indigo-700"
        title="Sicurezza & Password"
        subtitle="Modifica la password di accesso per questo account"
      >
        <form onSubmit={handleChangePassword} className="space-y-3 text-xs">
          <div className="space-y-1">
            <label className="block font-bold text-slate-700">Password Attuale</label>
            <input
              type="password"
              className="input text-xs font-semibold"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="Inserisci password attuale..."
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="space-y-1">
              <label className="block font-bold text-slate-700">Nuova Password</label>
              <input
                type="password"
                className="input text-xs font-semibold"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Almeno 6 caratteri..."
                required
              />
            </div>
            <div className="space-y-1">
              <label className="block font-bold text-slate-700">Conferma Nuova Password</label>
              <input
                type="password"
                className="input text-xs font-semibold"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti nuova password..."
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={changePwdLoading}
            className="btn btn-primary justify-center py-2.5 px-4 text-xs font-bold gap-1.5 shadow-xs"
          >
            {changePwdLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
            <span>Aggiorna Password</span>
          </button>
        </form>
      </CollapsibleSection>

      {/* ── DISCONNETTI ACCOUNT (collassato di default) ── */}
      <CollapsibleSection
        icon={LogOut}
        iconClass="bg-rose-100 text-rose-700"
        title="Sessione"
        subtitle="Esci dall'account della squadra"
      >
        <button
          type="button"
          className="btn-danger w-full justify-center py-3 text-sm gap-2"
          onClick={logout}
        >
          <LogOut className="w-4 h-4" />
          <span>Disconnetti account</span>
        </button>
      </CollapsibleSection>
    </div>
  );
}
